import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import {
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { XAxis, YAxis, ResponsiveContainer, Tooltip, Bar, BarChart, CartesianGrid } from "recharts";

const teamColors: { [id: string]: string[] } = {
  TBR: ["#02285C", "#90BDE7", "#FFFFFF"],
  BAL: ["#F25D23", "#000000", "#FFFFFF"],
  NYY: ["#132448", "#FFFFFF", "#FFFFFF"],
  BOS: ["#BD3039", "#0D2B56", "#FFFFFF"],
  TOR: ["#134A8E", "#E8291C", "#FFFFFF"],
  CLE: ["#E31937", "#002B5C", "#FFFFFF"],
  KCR: ["#004687", "#FFFFFF", "#FFFFFF"],
  DET: ["#0C2C56", "#FFFFFF", "#FFFFFF"],
  CHW: ["#000000", "#BFBFBF", "#FFFFFF"],
  MIN: ["#091F40", "#E31E34", "#FFFFFF"],
  HOU: ["#002D62", "#F4911E", "#FFFFFF"],
  SEA: ["#005C5C", "#0C2C56", "#FFFFFF"],
  TEX: ["#C0111F", "#003278", "#FFFFFF"],
  LAA: ["#BA0021", "#003263", "#C4CED4"],
  ATH: ["#00342C", "#FFB400", "#FFFFFF"],
  WSN: ["#AB0003", "#FFFFFF", "#FFFFFF"],
  NYM: ["#F75500", "#002C6E", "#FFFFFF"],
  MIA: ["#019CDB", "#E51829", "#FFFFFF"],
  PHI: ["#C21D39", "#01396F", "#FFFFFF"],
  ATL: ["#D3042B", "#001D3D", "#FFFFFF"],
  CIN: ["#C6011F", "#000000", "#FFFFFF"],
  PIT: ["#F7BF02", "#000000", "#FFFFFF"],
  CHC: ["#123581", "#E62F2E", "#FFFFFF"],
  MIL: ["#18294C", "#FEC430", "#FFFFFF"],
  STL: ["#BE0A15", "#FDDB00", "#FFFFFF"],
  LAD: ["#005A9D", "#FFFFFF", "#FFFFFF"],
  ARI: ["#C71128", "#00C2CE", "#FFFFFF"],
  SDP: ["#2D221C", "#FFC125", "#FFFFFF"],
  SFG: ["#231F20", "#F4793E", "#E8D8C4"],
  COL: ["#503E80", "#BFC2C9", "#FFFFFF"],
  FA: ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
  GDD: ["#F15C22", "#000000", "#FFFFFF"],
  MSS: ["#F4D12F", "#C80F2D", "#FFFFFF"],
  PEJ: ["#807E82", "#C1D72E", "#FFFFFF"],
  SRR: ["#008B98", "#B49A64", "#FFFFFF"],
  SCO: ["#C80F2D", "#000000ff", "#FFFFFF"],
  SUR: ["#49A942", "#BA0E2A", "#FFFFFF"],
  "0": ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
};

export const AFL_EVENT_COLORS: { [id: string]: string } = {
  "Groundout": "#BDBDBD",
  "Single": "#008000",
  "Double": "#0000FF",
  "Home Run": "#f7eb11",
  "GIDP": "#BDBDBD",
  "Lineout": "#BDBDBD",
  "Flyout": "#BDBDBD",
  "Field Error": "#BDBDBD",
  sac_fly: "#BDBDBD",
  sac_bunt: "#BDBDBD",
  "Triple": "#f42f11",
  double_play: "#BDBDBD",
  fielders_choice_out: "#BDBDBD",
  fielders_choice: "#BDBDBD",
};

export default function SwingSpeedChart(props: any) {
  const [swingData, setSwingData] = useState<any[]>([]);
  
  useEffect(() => {
    fetch(props.prod ? "https://oriolebird.pythonanywhere.com/afl-hitter-swing/" + props.id : "/afl-hitter-swing/" + props.id)
      .then((res) => res.json())
      .then((pData) => {
        //console.log("Swing Data for ", props.id, pData);
        setSwingData(pData)
      });
  }, [props.id, props.prod, props.season]);

  // Process swing data into histogram bins for batSpeed
  const { histogramData, averageSpeed, totalSwings } = useMemo(() => {
    if (!swingData || swingData.length === 0) return { histogramData: [], averageSpeed: 0, totalSwings: 0 };

    // Filter out null or invalid batSpeed values AND only include swings >= 50 mph
    const validData = swingData.filter(item => item.batSpeed != null && !isNaN(item.batSpeed) && item.batSpeed >= 50);
    
    if (validData.length === 0) return { histogramData: [], averageSpeed: 0, totalSwings: 0 };

    // Calculate average swing speed
    const speeds = validData.map(item => item.batSpeed);
    const averageSpeed = speeds.reduce((sum, speed) => sum + speed, 0) / speeds.length;
    const minSpeed = Math.min(...speeds);
    const maxSpeed = Math.max(...speeds);
    
    // Create bins (3 mph ranges for better granularity)
    const binSize = 3;
    
    // Calculate the number of bins needed, ensuring we cover the entire range including maxSpeed
    const range = maxSpeed - minSpeed;
    const binCount = Math.ceil(range / binSize) + 1; // +1 to ensure we include the top values
    
    // Initialize bins - we'll start from a rounded down value to make bins cleaner
    const startBin = Math.floor(minSpeed / binSize) * binSize;
    const bins = Array.from({ length: binCount }, (_, i) => {
      const binMin = startBin + i * binSize;
      const binMax = binMin + binSize;
      return {
        range: `${binMin}-${binMax}`,
        binMin,
        binMax,
        count: 0,
        percentage: 0,
        // For tooltip
        tooltipLabel: `${binMin}-${binMax} mph`
      };
    });
    
    // Count values in each bin - FIXED: properly assign all values including those at the upper bounds
    validData.forEach(item => {
      const speed = item.batSpeed;
      
      // Find the correct bin index
      let binIndex = Math.floor((speed - startBin) / binSize);
      
      // Ensure we don't go out of bounds (shouldn't happen with our +1 above, but just in case)
      if (binIndex >= bins.length) {
        binIndex = bins.length - 1;
      }
      
      if (binIndex >= 0 && binIndex < bins.length) {
        bins[binIndex].count++;
      }
    });

    // Convert counts to percentages
    bins.forEach(bin => {
      bin.percentage = (bin.count / validData.length) * 100;
    });

    // Filter out empty bins at the extremes to keep the chart clean
    const nonEmptyBins = bins.filter(bin => bin.count > 0);
    
    return {
      histogramData: nonEmptyBins,
      averageSpeed: Math.round(averageSpeed * 10) / 10,
      totalSwings: validData.length
    };
  }, [swingData]);

  const temp = Object.keys(props).includes('teamInfoProp')
  if (temp) {
    props.playerInfoProp.common = { teamInfo: props.teamInfoProp }
  }

  const renderHistogramTooltip = (props: any) => {
    const { active, payload } = props;

    if (active && payload && payload.length) {
      const data = payload[0] && payload[0].payload;

      return (
        <div
          style={{
            backgroundColor: '#fff',
            border: '1px solid #999',
            margin: 0,
            padding: 10,
          }}
        >
          <p><strong>{data.tooltipLabel || data.range}</strong></p>
          <p>Percentage: {data.percentage.toFixed(1)}%</p>
          <p>Swings: {data.count}</p>
        </div>
      );
    }

    return null;
  };

  const w = 350;

  // Get team colors for styling
  const teamColor = props.playerDataProp.afl_team !== null 
    ? teamColors[props.playerDataProp.afl_team][0] 
    : teamColors["FA"][0];
  
  const textColor = props.playerDataProp.afl_team !== null
    ? teamColors[props.playerDataProp.afl_team][1]
    : teamColors["FA"][1];

  return (
    <div>
      <Grid
        container
        justifyContent="center"
        spacing={2}
        marginTop={2}
      >
        <Grid maxWidth="90vw">
          <Box
            sx={{
              maxWidth: "90vw",
              width: "500px",
              border: "#293241 solid 2px",
              backgroundColor: "#FFFFFF",
              height: props.isDesktop ? "425px" : "100%",
              boxShadow:
                "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
            }}
            justifyContent="flex-start"
            alignItems="center"
            display="flex"
            flexDirection="column"
          >
            <div
              style={{
                width: "100%",
                paddingTop: "10px",
                paddingBottom: "10px",
                backgroundColor: teamColor,
              }}
            >
              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
              >
                <Typography variant="h5">
                  <span
                    style={{
                      color: textColor,
                      fontWeight: "bold",
                      fontSize: "25pt",
                      textAlign: "center"
                    }}
                  >
                    Swing Speed Distribution
                  </span>
                </Typography>
              </Grid>
            </div>
            
            {/* Histogram Chart */}
            <ResponsiveContainer width={w} height={w * .75} style={{ marginTop: 10 }}>
              <BarChart
                data={histogramData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="range"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  label={{ value: 'Bat Speed Range (mph)', position: 'insideBottom', offset: -10 }}
                />
                <YAxis
                  label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }}
                  domain={[0, 100]}
                  ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                />
                <Tooltip content={renderHistogramTooltip} />
                <Bar
                  dataKey="percentage"
                  fill={teamColor}
                  name="Percentage"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>

            {/* Average Swing Speed Display */}
            {totalSwings > 0 && (
              <Box sx={{ 
                width: "70%", 
                textAlign: "center", 
                padding: "10px",
                borderTop: "1px solid #e0e0e0",
                backgroundColor: "#f5f5f5",
                marginTop: -4
              }}>
                <Typography variant="h6" component="div">
                  Average Swing Speed * : <strong>{averageSpeed} mph</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  ({totalSwings} swings ≥ 50 mph *)
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </div>
  );
}